import { Info, Search, X, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DocPage } from "../../components/DocPage";
import { GateMeter } from "../../components/GateMeter";
import { Sheet } from "../../components/Sheet";
import { Button, DataList, EmptyState, Panel, StatusPill } from "../../components/primitives";
import { packageUrl } from "../../lib/api";
import { apiUrl } from "../../lib/env";
import { formatLevel } from "../../lib/format";
import { documentPreview, isInternalRun } from "../../lib/run";
import { humanizeToken } from "../../lib/status";
import { loadAuthoritativeConcept } from "../../lib/storage";
import type { RunStatus } from "../../lib/types";
import {
  INTERNAL_FILTER_WORDS,
  alternateStep,
  excerptOf,
  genericBlockerMessage,
  libraryQa,
  nextStep,
  packageValue,
  plural,
  sentenceCase
} from "./libraryModel";

/**
 * LIBRARY — /runs. A READING ROOM.
 *
 * Left: a scannable index of documents. Right: the document itself, rendered as a page of warm
 * paper resting on a lit desk. The chrome is quiet sans; the content is serif on --paper with a
 * real page shadow. That single figure/ground split is what makes the screen beautiful and what
 * makes it obviously a DOCUMENT product.
 *
 * ROUND 2 — WHAT THE CRITIC BROKE, AND WHY IT IS NOW FIXED
 *  - THE DEAD COLUMN MOVED, IT DID NOT DIE. The right column always held a document, but the
 *    inspector then ran 2,400-3,900px tall beside a 900px list, so two thirds of the LEFT
 *    column became void. Both columns are now ONE viewport-tall reading room: the index scrolls
 *    in its rail, the document scrolls in its panel, and the two columns end on the same y BY
 *    CONSTRUCTION, at every width. There is no void on either side to balance.
 *  - 834 WAS A DEAD-WIDE LIST. The cards stretched to ~1,330px with the pill, the QA line and
 *    the date marooned in 700px of empty white. The tablet is a TOUCH device (a tap opens the
 *    document in the Sheet, actions in the thumb zone), so it keeps the Sheet — but its list is
 *    now capped to a scannable reading column with real margins, not a full-bleed table.
 *  - THE FADE ATE A PROTECTED FACT. The excerpt was masked to transparent, and at 1440 the last
 *    line held the sealed "2021" citation — a protected fact rendered illegible in a product
 *    whose entire promise is that protected facts are never altered. The mask is GONE. The
 *    excerpt is shown whole, on a full page, and the sans note names it as an excerpt.
 *  - "NOT RUN" LOOKED LIKE A LOADING SKELETON. Six flat grey bars under "0 of 6 checks have a
 *    verdict" is the exact grey-skeleton language this pass bans. A not-run check is now a
 *    HOLLOW segment — the same hollow ring the gate list uses for the same word — and a visible
 *    legend states in words how many passed, how many did not pass, and how many never ran.
 *  - THE BLOCKED STATE WAS THREE COPIES OF ONE SENTENCE. The boilerplate consequence is stated
 *    ONCE, and the blocked checks are listed under it. Any message the BACKEND actually returned
 *    is still printed verbatim beside its check — nothing real is ever collapsed away.
 *
 * WHAT IS UNTOUCHABLE, AND SURVIVES BYTE-FOR-BYTE
 *    libraryQa() is the ONLY source of every QA figure on this screen — never run.status, never
 *    a percentage, never a tick without a backend verdict. The meter draws what did NOT run as
 *    an empty segment and has no code path that can draw it as a pass. The internal-run filter
 *    still names its own heuristic verbatim and still offers the escape hatch. The download still
 *    requires apiUrl AND a real package.
 */

/**
 * >=1024 is where the Sheet retires and the detail becomes a persistent column. ONE source of
 * truth, and it is 1024 rather than 768 on purpose: 834 is a TOUCH tablet (the smoke harness
 * treats it as one), so a tap on a document opens the focused Sheet, with its actions in the
 * thumb zone, exactly as it does on the phone. The tablet does not get a bare, dead-wide list
 * instead — its list is capped to a scannable reading column (see library.css @768).
 */
const DESKTOP_QUERY = "(min-width: 1024px)";

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches
  );
  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY);
    const sync = () => setIsDesktop(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  return isDesktop;
}

/**
 * THE BLOCKING MESSAGES, GROUPED BY THE CHECK THAT PRODUCED THEM.
 *
 * `gateBlockers()` returns one entry PER MESSAGE, which is right for a reason list and wrong for
 * a count: a single gate that returns two issues is still ONE check that did not clear. Grouping
 * is what lets the panel state the number of failing CHECKS — the same figure, from the same
 * gates, that the list card already prints — instead of the number of complaints.
 *
 * It adds nothing. The generated boilerplate is dropped (it is the same sentence for every gate,
 * and the card already says it once, above); every message the BACKEND actually returned is kept
 * verbatim, in order. A gate whose only message was boilerplate keeps its row and simply carries
 * no reason — because it HAS no reason from the backend, and inventing one is the one thing this
 * panel may never do.
 */
function groupBlockersByGate(blockers: ReturnType<typeof libraryQa>["blockers"]) {
  const byGate = new Map<string, { gateId: string; gateLabel: string; messages: string[] }>();
  for (const blocker of blockers) {
    let group = byGate.get(blocker.gateId);
    if (!group) {
      group = { gateId: blocker.gateId, gateLabel: blocker.gateLabel, messages: [] };
      byGate.set(blocker.gateId, group);
    }
    if (blocker.message === genericBlockerMessage(blocker.gateLabel)) continue;
    if (!group.messages.includes(blocker.message)) group.messages.push(blocker.message);
  }
  return [...byGate.values()];
}

/* ------------------------------------------------------------------ THE INSPECTOR
   ONE markup, TWO homes: the bottom Sheet below 1024, the right column at >=1024.

   Order inside: the PAGE first (on its desk), then the trust band, then the run's data, then
   the actions at the natural end. The document is the subject; everything else is about it. */

function Inspector({
  run,
  surface,
  actions
}: {
  run: RunStatus;
  surface: "sheet" | "column";
  actions?: React.ReactNode;
}) {
  const preview = documentPreview(run);
  const qa = libraryQa(run);
  const blockedGates = groupBlockersByGate(qa.blockers);

  const body = (
    <>
      {/* ROUND 3 — THE SCROLL BOUNDARY IS EXPLICIT.
          Everything ABOUT the document scrolls; the ACTIONS ON it do not. At >=1024 this
          wrapper is the panel's one scroll region and .inspector-actions is a grid row beneath
          it, so the primary action ("Review edits" / "Open editor" / "Download package") is on
          screen at rest, at every desktop height, without the operator first scrolling the body
          and then discovering a nested scroll container. It is a PANEL FOOTER, not a floating
          bar: it occupies its own track and can never overlap the content above it. */}
      <div className="inspector-scroll">
        {/* THE DESK. The panel's own ground is --surface; this band is a lit desk, and the page
            floats on it at --elev-doc. Nothing else on the screen lifts that far, so the document
            is unambiguously the most important object in the room. */}
        <div className="inspector-document">
          <DocPage
            org={run.input.organization}
            title={preview.title}
            subtitle={preview.subtitle}
            /* NO MASK, NO CLAMP, NO ELLIPSIS. The excerpt is shown WHOLE. The previous fade
               dissolved its final line — which at 1440 contained a sealed protected citation —
               and a protected fact that cannot be read is the one thing this product may never
               render. The sans note below names what the reader is looking at instead. */
            body={excerptOf(run)}
            note="Excerpt from the first editable section. Open the editor for the full document."
            protectedFacts={run.document.protectedFacts || []}
            testId="inspector-preview"
            bodyTestId="inspector-copy"
            footTestId="inspector-protected"
          />
        </div>

        {/* THE TRUST BAND. The pill states the HUMAN decision; the sentence states the MACHINE
            verdict; the meter and its legend show, per check, how much of the machine's work
            actually ran. Once each, never conflated, and none of them can express a percentage —
            a not-run gate is an EMPTY segment and there is no code path that draws it as a pass. */}
        <div className="inspector-state">
          <div className="inspector-state-head">
            <StatusPill status={run.status} />
            <p className="inspector-qa" data-testid="inspector-qa">
              {qa.sentence}
            </p>
          </div>

          <div className="inspector-gates">
            <GateMeter gates={qa.gates} running={false} />
            {/* THE LEGEND. Without it, six empty segments are indistinguishable from a loading
                skeleton. Every figure here comes from gateCounts() and a zero is never printed. */}
            <ul className="inspector-legend">
              {qa.counts.passed ? (
                <li data-state="passed">
                  <span className="inspector-legend-swatch" aria-hidden="true" />
                  {qa.counts.passed} passed
                </li>
              ) : null}
              {qa.counts.failed ? (
                <li data-state="failed">
                  <span className="inspector-legend-swatch" aria-hidden="true" />
                  {qa.counts.failed} did not pass
                </li>
              ) : null}
              {qa.counts.notRun ? (
                <li data-state="not_run">
                  <span className="inspector-legend-swatch" aria-hidden="true" />
                  {qa.counts.notRun} not run
                </li>
              ) : null}
            </ul>
          </div>

          {/* BLOCKED. The shared consequence is stated ONCE and the blocked checks are listed
              under it. A message the BACKEND actually returned is printed verbatim beside its
              check; only the generated boilerplate — which was identical on all three cards — is
              said once. A FULL border, so it is unmissable without an edge rail.

              IT COUNTS CHECKS, NOT COMPLAINTS. `qa.blockers` is a flat list of MESSAGES, so
              `blockers.length` counts issues — and one gate that returned two issues was
              announced here as "2 checks did not clear" while the very card that opened this
              panel said "Final QA blocked on 1 check". The same screen stated the same QA figure
              two different ways, and the bigger of the two was the wrong one. The messages are
              grouped by the gate that produced them: the heading counts GATES, each gate is
              named ONCE, and every backend message it returned is printed verbatim beneath it.
              Nothing is dropped and nothing is inflated. */}
          {blockedGates.length ? (
            <div className="inspector-blocked" data-testid="inspector-blockers">
              <p className="inspector-blocked-head">
                <XCircle aria-hidden="true" />
                {plural(blockedGates.length, "check", "checks")} did not clear
              </p>
              <p className="inspector-blocked-copy">
                Export is blocked. Each of these needs a repair or a reviewer decision.
              </p>
              <ul className="inspector-blocked-list">
                {blockedGates.map((gate) => (
                  <li key={gate.gateId}>
                    <strong>{gate.gateLabel}</strong>
                    {gate.messages.map((message) => (
                      <span key={message}>{message}</span>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="inspector-details">
          <h3 className="inspector-details-title">Run details</h3>
          <div data-testid="inspector-meta">
            <DataList
              label="Run details"
              items={[
                [
                  "Personalization",
                  `${run.input.personalizationLevel} · ${sentenceCase(formatLevel(run.input.personalizationLevel))}`
                ],
                ["Purpose", sentenceCase(humanizeToken(run.input.purpose))],
                ["Run mode", sentenceCase(humanizeToken(run.input.mode))],
                [qa.dateLabel, qa.date],
                ["Package", packageValue(run)],
                ["Run ID", <code className="inspector-run-id">{run.runId}</code>]
              ]}
            />
          </div>
        </div>
      </div>

      {actions ? <div className="inspector-actions">{actions}</div> : null}
    </>
  );

  if (surface === "sheet") {
    return (
      <div className="library-inspector" data-surface="sheet" data-testid="library-inspector">
        {body}
      </div>
    );
  }
  return (
    <Panel className="library-inspector" data-surface="column" data-testid="library-inspector">
      {body}
    </Panel>
  );
}

/** DataList rows carry --panel-pad, so every value shares one right edge with the row above. */
function InspectorActions({ run }: { run: RunStatus }) {
  const primary = nextStep(run);
  const secondary = alternateStep(run, primary);
  const pkg = run.finalCopy?.package;

  return (
    <>
      <Link className="btn btn-primary btn-lg btn-block" to={primary.to} data-testid={primary.testId}>
        {primary.label}
      </Link>
      <Link className="btn btn-secondary btn-md btn-block" to={secondary.to} data-testid={secondary.testId}>
        {secondary.label}
      </Link>
      {/* THE EXPORT SURFACE DOES NOT WIDEN. The condition that produces a WORKING download is
          byte-identical to today: `apiUrl && run.finalCopy?.package`, and nothing else. The
          Library is never a shortcut past the QA gate, and no CARD may link to a package — only
          the detail.

          WHAT CHANGED IS WHAT HAPPENS WHEN IT IS NOT MET. In local preview (apiUrl === "") the
          action was not rendered AT ALL, so the one operation the operator is ultimately here to
          perform simply did not exist, with no statement of why. A capability that silently
          vanishes teaches nothing; a disabled control that NAMES its blocker teaches exactly
          what is missing. It is strictly more honest and it opens no door: the anchor — the only
          thing that can actually fetch a package — is still gated on a backend AND a real
          package, and each reason below states the true blocker rather than a generic one. */}
      {apiUrl && pkg ? (
        <a
          className="btn btn-secondary btn-md btn-block"
          data-testid="library-action-package"
          href={packageUrl(run.runId)}
        >
          Download package
        </a>
      ) : (
        <Button
          variant="secondary"
          size="md"
          block
          disabled
          data-testid="library-action-package"
          reason={
            apiUrl
              ? "No package yet. Approve every edit and run final QA in Review, then export."
              : "Local preview has no backend, so no package can be fetched. Connect an API in Settings to export."
          }
        >
          Download package
        </Button>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ THE CARD
   THE INDEX ENTRY, and its height is EQUAL BY CONSTRUCTION. Every row on it is exactly one
   line except the serif title, which clamps to two AND reserves two — so no card can grow or
   shrink a line, and the list keeps a true scan line at every width.

   ROUND 2: the old card reserved TWO lines for the QA sentence and then put the date on its own
   row beneath it, which opened a visible dead gap inside every card on the phone. Nothing is
   reserved now. The QA line and the date are a single tight caption pair, each one line, each
   with room to state itself in full at 320px — the honest QA sentence is never cut to make a
   layout work.

   ONE unambiguous target filling the card. No nested interactive element, no per-card action
   row (12 cards would become 24 buttons), and NO CHECKMARK: selection is not a verdict, and a
   tick is the glyph the pill uses for "Approved". Selected is a FULL accent ring, all four
   sides. Never an edge rail. */

function LibraryCard({ run, selected, onSelect }: { run: RunStatus; selected: boolean; onSelect: () => void }) {
  const preview = documentPreview(run);
  const qa = libraryQa(run);
  const initial = run.input.organization.trim().charAt(0).toUpperCase();

  return (
    <button
      type="button"
      className="panel library-row"
      data-testid="library-row"
      data-selected={selected ? "true" : "false"}
      data-run-id={run.runId}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
    >
      {/* Decorative geometry plus ONE real letter. It asserts nothing and fabricates nothing —
          it gives the list the silhouette of a shelf of documents. */}
      <span className="library-row-thumb" aria-hidden="true">
        {initial}
      </span>
      <span className="library-row-org">{run.input.organization}</span>
      {/* SERIF. The index reads as documents, not as a settings list, at zero honesty cost.
          The OUTER span is the reserved two-line box (so every card is the same height whatever
          its title does); the INNER span is the clamped text, CENTRED in that box. Round 2
          reserved the two lines on the text node itself, which parked a one-line title hard
          against the org above it and left the whole reserve as a dead ~23px gap before the
          pill. Same reserve, same equal heights — but the slack is now split above and below
          the title, so the interior rhythm of a one-line card and a two-line card agree. */}
      <span className="library-row-title">
        <span className="library-row-title-text">{preview.title}</span>
      </span>
      {/* The pill takes its OWN row. It is the widest variable-width object on the card
          ("Blocked — human review") and sharing a row with the organization is what forced a
          funder's name to hyphenate mid-word in a 320px rail. A name is the identity of a
          document; it does not get broken by a layout accident. */}
      <span className="library-row-pill">
        <StatusPill status={run.status} />
      </span>
      <span className="library-row-meta">
        {/* Derived from gateCounts(deriveGates(run, "idle")) and from nothing else — never from
            run.status, never a percentage, never a tick. */}
        <span className="library-row-qa">{qa.cardState}</span>
        <span className="library-row-date">
          {qa.dateLabel} {qa.date}
        </span>
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ THE SCREEN */

export function RunsPage({ runs }: { runs: RunStatus[] }) {
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"customer" | "all">("customer");
  const [selectedRunId, setSelectedRunId] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const isDesktop = useIsDesktop();

  const term = query.trim().toLowerCase();

  const { visibleRuns, poolCount, hiddenCount } = useMemo(() => {
    const pool = runs.filter((run) => view === "all" || !isInternalRun(run));
    const matches = (run: RunStatus) =>
      !term ||
      `${run.input.organization} ${run.input.purpose} ${run.document.title} ${run.runId}`.toLowerCase().includes(term);
    const byRecency = (a: RunStatus, b: RunStatus) =>
      new Date(b.document.updatedAt).getTime() - new Date(a.document.updatedAt).getTime();
    return {
      visibleRuns: pool.filter(matches).sort(byRecency),
      poolCount: pool.length,
      hiddenCount: view === "customer" ? runs.filter(isInternalRun).length : 0
    };
  }, [runs, term, view]);

  /**
   * THE END OF THE EMPTY VOID.
   *
   * `selectedRun` is the operator's EXPLICIT choice and it is what opens the Sheet — so
   * auto-selection can never pop a sheet nobody asked for. `activeRun` is what the DESKTOP
   * inspector renders, and it falls back to the most recent visible run, so the document column
   * always holds a real document while >=1 run is visible. It also survives a search or a filter
   * change that removes the selected run: the fallback simply re-resolves.
   */
  const selectedRun = visibleRuns.find((run) => run.runId === selectedRunId);
  const activeRun = isDesktop ? selectedRun || visibleRuns[0] : selectedRun;

  function select(run: RunStatus) {
    setSelectedRunId(run.runId);
    setDetailOpen(true);
  }

  function showAllRuns() {
    setView("all");
  }

  /* ---------------------------------------------------------------- ZERO RUNS
     The document a run personalizes, rendered as the page it is — so the empty state TEACHES
     the product in one look instead of drawing a grey file glyph. It states only what is
     KNOWN about the source: its title, whether it is the canonical or a workspace source, its
     real excerpt and its file type. No page count, no size and no date is invented. */
  if (!runs.length) {
    const concept = loadAuthoritativeConcept();
    return (
      <section className="library-page" data-testid="library-page" data-state="empty">
        <Panel className="library-blank" role="status" data-testid="empty-state">
          <div className="library-blank-desk">
            <DocPage
              title={concept.title}
              subtitle={concept.mode === "workspace" ? "Workspace source" : "Canonical source"}
              body={concept.textExcerpt || undefined}
              note={concept.fileType}
            />
          </div>
          <div className="library-blank-copy">
            <h3>No documents yet</h3>
            <p>
              Create a run to personalize this note for one funder. You review every proposed edit
              before the document is created.
            </p>
            <Link className="btn btn-primary btn-lg" to="/new">
              Start a run
            </Link>
          </div>
        </Panel>
      </section>
    );
  }

  const countLine = term
    ? `${visibleRuns.length} of ${plural(poolCount, "document", "documents")} match “${query.trim()}”`
    : plural(visibleRuns.length, "document", "documents");

  return (
    <section className="library-page" data-testid="library-page" data-state="ready">
      {/* Sticky. In a list screen this is the single biggest phone win: search never scrolls
          away, so a 50-run library stays navigable one-handed. Authored in the base layer with
          no media query — the base layer IS the phone. At >=768 the three controls sit on ONE
          row, stretched to ONE shared height, sharing the content column's right edge. */}
      <div className="library-filters">
        <div className="library-search-row">
          <label className="search-field">
            <Search aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              /* SHORT placeholder: the full string was clipped at 320 and 390, and a clipped
                 placeholder is a control hiding its own content. The aria-label keeps the
                 complete description for assistive technology. */
              placeholder="Search documents"
              aria-label="Search organization, document, or run ID"
              data-testid="library-search"
            />
          </label>
          {query ? (
            <Button variant="ghost" size="md" aria-label="Clear search" onClick={() => setQuery("")}>
              <X aria-hidden="true" />
            </Button>
          ) : null}
        </div>

        {/* Segmented is correct HERE and only here: a view toggle is cheap, reversible and
            non-governed, so a bare label is honest. Its CONSEQUENCE is stated below, because
            this one option changes what you can see. */}
        <div className="segmented library-view" role="group" aria-label="Library view" data-testid="library-view">
          <button
            type="button"
            className={view === "customer" ? "active" : ""}
            aria-pressed={view === "customer"}
            data-testid="library-view-option"
            data-value="customer"
            onClick={() => setView("customer")}
          >
            Customer
          </button>
          {/* HARNESS: this button keeps data-testid="library-internal-toggle" so the existing
              single click that reveals the internal run still works unchanged. */}
          <button
            type="button"
            className={view === "all" ? "active" : ""}
            aria-pressed={view === "all"}
            data-testid="library-internal-toggle"
            data-value="all"
            onClick={showAllRuns}
          >
            All runs
          </button>
        </div>

        {/* >=768 only. On phone this affordance is the tab bar's "New" plus the primary block
            button at the END of the list — never a third, orphaned copy floating under it. */}
        <Link className="btn btn-primary btn-md library-new" to="/new">
          New run
        </Link>
      </div>

      <div className="library-meta">
        <p className="library-count" data-testid="library-count">
          {countLine}
        </p>

        {/* The internal-run filter is a heuristic that can hide a real customer whose name
            contains "Test", "Check" or "QA". A library that can make a document disappear by
            heuristic and not say so is not trustworthy, so the sentence names the heuristic
            verbatim and the escape hatch is always one tap away. It is a NOTICE, not a paragraph
            with a 44px button jammed into its line box. */}
        {hiddenCount ? (
          <div className="library-notice" data-testid="library-filter-consequence">
            <Info aria-hidden="true" />
            <p>
              {plural(hiddenCount, "run", "runs")} hidden. The internal-run filter hides any run whose
              organization, title or ID contains {INTERNAL_FILTER_WORDS} — a real organization with one
              of those words in its name is hidden too.
            </p>
            <button type="button" className="library-notice-action" onClick={showAllRuns}>
              Show all runs
            </button>
          </div>
        ) : null}
      </div>

      {/* THE READING ROOM. At >=768 both columns are one viewport-tall row: the index scrolls in
          its rail, the document scrolls in its panel, and the two columns END ON THE SAME Y by
          construction. Neither side can run 2,000px past the other and leave a void — which is
          exactly what the first attempt did.

          When nothing matches, there is no document to inspect, so the workspace collapses to ONE
          column and the blank state gets the whole width. A half-empty rail beside 1,000px of
          nothing is the dead column this pass exists to kill; it is not allowed back in through
          the empty state. */}
      <div className="library-workspace" data-results={visibleRuns.length ? "some" : "none"}>
        <div className="library-list-column">
          {visibleRuns.length ? (
            <>
              <div className="library-list" data-testid="library-list">
                {visibleRuns.map((run) => (
                  <LibraryCard
                    key={run.runId}
                    run={run}
                    selected={activeRun?.runId === run.runId}
                    onSelect={() => select(run)}
                  />
                ))}
              </div>
              {/* Phone only, and PRIMARY: the main creative act of the product may not be the
                  quietest control on the screen. Full width, on the list's own grid edges, as
                  the last item of the stack — it is not an orphan hanging under the list, it is
                  the end of it. */}
              <Link className="btn btn-primary btn-md btn-block library-new-inline" to="/new">
                New run
              </Link>
            </>
          ) : (
            /* The list may NEVER appear empty while a filter is silently removing rows. */
            <Panel className="library-blank library-blank-search">
              <EmptyState
                glyph={<Search aria-hidden="true" />}
                title={term ? `No documents match “${query.trim()}”` : "No customer documents"}
                body={
                  term
                    ? "Try a different organization, document title, or run ID."
                    : "Every run in this library is hidden by the internal-run filter."
                }
                action={
                  hiddenCount ? (
                    <>
                      <p className="library-blank-note">
                        {plural(hiddenCount, "run is", "runs are")} hidden by the internal-run filter.
                      </p>
                      <Button variant="secondary" size="md" onClick={showAllRuns}>
                        Show all runs
                      </Button>
                    </>
                  ) : null
                }
              />
            </Panel>
          )}
        </div>

        {/* >=1024: the Sheet retires and the SAME inspector markup becomes the document column.
            It is NEVER empty while a run is visible. Below 1024 nothing is rendered here — the
            detail is a Sheet, opened only by an explicit tap. */}
        {isDesktop && activeRun ? (
          <div className="library-detail-column">
            <Inspector run={activeRun} surface="column" actions={<InspectorActions run={activeRun} />} />
          </div>
        ) : null}
      </div>

      {!isDesktop && detailOpen && selectedRun ? (
        <Sheet
          title={selectedRun.input.organization}
          testId="library-detail-sheet"
          onClose={() => setDetailOpen(false)}
          footer={<InspectorActions run={selectedRun} />}
        >
          <Inspector run={selectedRun} surface="sheet" />
        </Sheet>
      ) : null}
    </section>
  );
}
